<template>
  <AdminLayout>
    <div v-if="!loadingSpinnerStore.loadingSpinner" class="flex flex-col">
      <div class="mb-4">
        <TeamListFilters :filters="filters" @update-filter="handleNewFilter" />
      </div>

      <Card class="gap-0 py-0 overflow-hidden">
        <DataTable
          :columns="dtColumns"
          :rows="rows"
          :click-fn="viewTeam"
          :link-fn="teamLink"
          :sort="sort"
          sticky-first-column
          @sort-updated="handleNewSort"
        >
          <template #td-company="slotProps">
            <div class="flex items-center">
              <div>
                <div class="text-sm leading-5 font-medium text-gray-900 truncate">
                  {{ slotProps.row.company }}
                </div>
                <div class="text-sm leading-5 text-gray-500 truncate">
                  {{ slotProps.row.email_reply_to }}
                </div>
              </div>
            </div>
          </template>

          <template #td-setup_progress="slotProps">
            <RadialProgress style="--size: 2rem; font-size: 0.5rem" :progress="slotProps.row.setup_progress" />
          </template>

          <template #td-active_customer="slotProps">
            <Badge :variant="!slotProps.row.active_customer ? 'red' : 'green'">
              {{ slotProps.row.active_customer ? 'Active' : 'Inactive' }}
            </Badge>
          </template>

          <template #td-status="slotProps">
            <Badge :variant="slotProps.row.status === 'Inactive' ? 'red' : 'green'">
              {{ slotProps.row.status }}
            </Badge>
          </template>
        </DataTable>
      </Card>
    </div>

    <LoadingList />

    <Pagination :start-entry="startEntry" :end-entry="endEntry" :total="total" />
  </AdminLayout>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import AdminLayout from '@/components/layouts/AdminLayout/index.vue';
import LoadingList from '../shared/LoadingList.vue';
import DataTable from '../shared/DataTable/DeprecatedDataTable.vue';
import TeamListFilters from './TeamListFilters.vue';
import { formatCurrencyToHuman, formatStandardDateTime } from '../../utilities/format.ts';
import RadialProgress from '../shared/RadialProgress.vue';
import Pagination from '../shared/Pagination.vue';
import { useLoadingSpinnerStore } from '@/stores/loadingSpinner.ts';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';

interface Team {
  id: number;
  company: string;
  email_reply_to: string;
  setup_progress: number;
  active_customer: boolean;
  status: string;
  stripe_total_mrr: number;
  stripe_total_spend: number;
  cost_per_candidate: number;
  cost_per_hire: number;
  number_of_agents: number;
  number_of_candidates_last_30_days: number;
  number_of_candidates_last_90_days: number;
  number_of_pipeline_reviews_last_30_days: number;
  created_at: string;
}

const router = useRouter();
const route = useRoute();
const loadingSpinnerStore = useLoadingSpinnerStore();

const teams = ref<Team[]>([]);
const startEntry = ref(0);
const endEntry = ref(0);
const total = ref(0);

const dtColumns = computed(() => [
  { label: 'Company', key: 'company', sortable: true },
  { label: 'Setup Progress', key: 'setup_progress', sortable: true },
  { label: 'Customer Status', key: 'active_customer', sortable: true },
  { label: 'Syndication Status', key: 'status', sortable: true },
  { label: '$ MRR', key: 'stripe_total_mrr', formatter: formatCurrencyToHuman, sortable: true },
  { label: '$ Spent', key: 'stripe_total_spend', formatter: formatCurrencyToHuman, sortable: true },
  { label: '$ per Candidate', key: 'cost_per_candidate', formatter: formatCurrencyToHuman, sortable: true },
  { label: '$ per Hire', key: 'cost_per_hire', formatter: formatCurrencyToHuman, sortable: true },
  { label: '# of Agents', key: 'number_of_agents', sortable: false },
  { label: '# of Candidates Last 30 Days', key: 'number_of_candidates_last_30_days', sortable: false },
  { label: '# of Candidates Last 90 Days', key: 'number_of_candidates_last_90_days', sortable: false },
  { label: '# of Pipeline Reviews Last 30 Days', key: 'number_of_pipeline_reviews_last_30_days', sortable: true },
  { label: 'Created', key: 'created_at', formatter: formatStandardDateTime, sortable: true },
]);

const rows = computed(() => teams.value);

const sort = computed(() => ({
  key: route.query.sort as string,
  order: route.query.order as string,
}));

const filters = computed(() => [{ key: 'status', value: (route.query.status as string) || 'All' }]);

function teamLink(record: Team) {
  return `/admin/teams/${record.id}`;
}

async function viewTeam(record: Team) {
  await router.push({ name: 'Team Admin', params: { id: record.id } });
}

async function getTeams() {
  loadingSpinnerStore.showLoadingSpinner();
  const request = await fetch(`/api/${route.fullPath}`);
  const data = await request.json();
  loadingSpinnerStore.hideLoadingSpinner();
  if (data.records && Array.isArray(data.records)) {
    teams.value = data.records;
    for (const team of teams.value) {
      team.stripe_total_spend = team.stripe_total_spend / 100;
      team.stripe_total_mrr = team.stripe_total_mrr / 100;
    }
  } else {
    teams.value = [];
  }
  startEntry.value = data.start_entry;
  endEntry.value = data.end_entry;
  total.value = data.total;
}

async function handleNewSort(newSort: { key: string; order: string }) {
  await router.push({
    path: '/admin/teams',
    query: { sort: newSort.key.toLowerCase(), order: newSort.order, status: route.query.status },
  });
  await getTeams();
}

async function handleNewFilter(newFilters: { status: { value: string } }) {
  const query = {
    sort: 'created',
    order: 'desc',
    status: newFilters.status.value || 'All',
  };
  await router.push({ path: '/admin/teams', query });
  await getTeams();
}

onMounted(async () => {
  await getTeams();
});
</script>
